import { stores } from '@stores'
import { AddProductToOrderService } from '../../../services/addProductToOrder'
import { PaginatedHierarchyItemsDataStore } from '@stores/paginated-hierarchy-items/PaginatedHierarchyItemsDataStore'
import { action, computed, observable, transaction } from 'mobx'
import { computedFn } from 'mobx-utils'
import { ChannelSubSection, OrderType } from '@modules/common/models/enums'
import getActivityHierarchyMap from '../../../utils/get-activity-hierarchy-map'
import { orderKPIGlobalTopBarStore } from '../../OrderKPIGlobalTopBarStore'
import isNil from 'lodash/isNil'
import { VIPClientType } from '@modules/common/models/enums/VIPClientType'
import { getEnumValue } from '@utils/get-enum-value'
import { getChannelName, getTitleCaseChannelName } from '@utils/get-channel-name'
import map from 'lodash/map'
import { config } from '@config'
import {
  MANDATORY_ORDER_ATTRIBUTE_FIELDS,
  PRODUCT_SERVICE_ENDPOINTS
} from '@modules/showroom/productCollection/enhancedView/constants/cnf-product-constants'
import {
  addHierarchyNamesToProductsData,
  formatProductGroupsData,
  getCurrentGroupMapping,
  mapNewFiltersToIds,
  parseAppliedSort,
  removeSkippedGroups
} from '@modules/order/utils/dynamic-group-mapping'
import { isEmptyValue } from '@utils/object-utils'
import { SORT_ORDERS } from '@components/UI-Components/EnhancedView/Sort/sort_constants'
import cloneDeep from 'lodash/cloneDeep'
import uniq from 'lodash/uniq'
import set from 'lodash/set'

import {
  CATEGORY_INTERNAL_SEPERATOR,
  CLIENT_KPI_ORDER_LEVEL_AGGR,
  CLIENT_ORDER_LEVEL_GROUP_ATTR,
  DOOR_KPI_ORDER_LEVEL_AGGR,
  DOOR_ORDER_LEVEL_GROUP_ATTR,
  KPI_TOPBAR_METRIC_ATTRIBUTES,
  METRIC_ATTRIBUTES
} from '@modules/order/constants/uiConstants'
import { OrderService } from '@modules/order/services/orderService'

export class AddProductToOrderDataStore extends PaginatedHierarchyItemsDataStore {
  @observable currentViewName = 'addProductToOrder'
  private apiService = new AddProductToOrderService()
  private orderService = new OrderService()
  @observable initialTotalProducts = 0
  @computed get isEnhancedViewEnabled() {
    const {
      productConfiguration: { enhancedViewEnabled }
    } = stores
    return enhancedViewEnabled
  }

  @action
  setInitialTotalProducts = value => {
    this.initialTotalProducts = value || 0
  }

  /**
   * Fetch product by hierachy details. We must load data by pagination instead of data by hierarchy level for case when particular
   * hierachy have huge data which will create issues in processing. so keeping above method to support the logic or better understanding of idea
   *
   * @param activity
   * @param family
   * @param line
   * @param fromIndex
   * @param toIndex
   */
  fetchItemsFromAPI = (fromIndex: number, toIndex: number) => {
    const {
      nav: {
        queryParams: {
          buyingSessionGroupId,
          orderId,
          orderType,
          clientOrderId,
          clientType,
          clientId,
          buyingSession
        }
      }
    } = stores

    if (this.isEnhancedViewEnabled) {
      return this.fetchItemsFromNewAPI({
        fromIndex,
        toIndex,
        buyingSessionGroupId,
        orderId,
        orderType,
        clientOrderId,
        clientId,
        clientType,
        buyingSession
      })
    }
    return this.fetchItemsFromOldAPI({
      fromIndex,
      toIndex,
      buyingSessionGroupId,
      orderId,
      orderType
    })
  }

  fetchItemsFromOldAPI = ({
    fromIndex,
    toIndex,
    buyingSessionGroupId,
    orderId,
    orderType
  }) => {
    const { activity, family, line } = getActivityHierarchyMap(
      this.hierarchyInRange(fromIndex, toIndex, this.heirarchy, 0).slice()
    )
    return this.apiService.fetchCollectionProductsByHierarchy(
      buyingSessionGroupId,
      orderId,
      orderType,
      {
        ...this.mapFiltersToIds(this.filterStore.appliedFilters),
        activityName: [activity.name],
        ...(family ? { familyName: [family.name] } : {}),
        ...(line ? { lineName: [line.name] } : {})
      },
      this.searchKey
    )
  }

  fetchItemsFromNewAPI = (params: any) => {
    const {
      fromIndex,
      toIndex,
      buyingSessionGroupId,
      orderId,
      orderType,
      clientOrderId,
      clientType,
      clientId,
      buyingSession
    } = params

    const {
      productConfiguration: {
        defaultValuesFromConfig,
        getViewProductCardAttributes,
        getActionAttributes,
        findSpecificAttribute
      }
    } = stores
    let mapping = getCurrentGroupMapping(
      this.hierarchyInRange(fromIndex, toIndex, this.heirarchy, 0),
      defaultValuesFromConfig?.empty,
      findSpecificAttribute
    )
    const channelName = getChannelName(clientType)
    const attrConfig = getViewProductCardAttributes(channelName, this.currentViewName)
    const searchableAttributes = getActionAttributes(
      'searchBy',
      channelName,
      this.currentViewName,
      true
    )
    const isRetail = channelName === ChannelSubSection.Retail
    const body = {
      sort: parseAppliedSort(this.enhancedSortStore.selectedSortOption),
      filters: {
        ...(mapping ? mapping : {}),
        ...mapNewFiltersToIds(this.cnfProductFilterStore.selected),
        ...(isRetail ? { 'buyingSession-id': [buyingSession] } : {})
      },
      excludeFilters: {
        ...mapNewFiltersToIds(this.cnfProductFilterStore.excludedFilters || {})
      },
      orderProductOrFilters: {
        filters: {
          [defaultValuesFromConfig?.isActive ?? 'original-active']: [true],
          channels: [getTitleCaseChannelName(clientType)],
          allClustersExcluded: [false]
        },
        excludeFilters: {
          'excludedClients-id': [clientId]
        }
      },
      ...(isEmptyValue(this.searchKey)
        ? {}
        : {
            search: this.searchKey,
            searchAttributes: this.searchKey ? searchableAttributes : []
          }),
      attributes: uniq(
        MANDATORY_ORDER_ATTRIBUTE_FIELDS.concat(attrConfig.map(item => item.attributeId))
      )
    }
    return this.apiService
      .fetchAddProductsToOrderByGrouping(
        buyingSessionGroupId,
        orderId,
        orderType,
        clientOrderId,
        body
      )
      .then(resp => {
        return addHierarchyNamesToProductsData(resp.data, mapping)
      })
  }

  fetchHierarchyFromAPI = (forRefresh?: boolean) => {
    const {
      nav: {
        queryParams: {
          buyingSessionGroupId,
          orderId,
          orderType,
          clientOrderId,
          clientType,
          clientId,
          buyingSession
        }
      }
    } = stores

    if (!orderId) return undefined
    if (this.isEnhancedViewEnabled) {
      return this.fetchHierarchyFromNewAPI(
        {
          buyingSessionGroupId,
          orderId,
          orderType,
          clientOrderId,
          clientId,
          clientType,
          buyingSession
        },
        forRefresh
      )
    }
    return this.fetchHierarchyFromOldAPI({
      buyingSessionGroupId,
      orderId,
      orderType,
      clientOrderId
    })
  }

  fetchHierarchyFromOldAPI = ({
    buyingSessionGroupId,
    orderId,
    orderType,
    clientOrderId
  }) => {
    return this.apiService.getCollectionHierarchy(
      buyingSessionGroupId,
      orderId,
      orderType,
      this.mapFiltersToIds(this.filterStore.appliedFilters),
      this.searchKey,
      clientOrderId
    )
  }

  /* This function fetches hierarchy data for new enhanced view API */
  fetchHierarchyFromNewAPI = (
    {
      buyingSessionGroupId,
      orderId,
      orderType,
      clientOrderId,
      clientType,
      clientId,
      buyingSession
    },
    forRefresh?: boolean
  ) => {
    const {
      productConfiguration: { defaultValuesFromConfig, getActionAttributes }
    } = stores

    const addedGroups = this.cnfGroupByUIStore.addedGroupByAttributes
    const channelName = getChannelName(clientType)
    const searchableAttributes = getActionAttributes(
      'searchBy',
      channelName,
      this.currentViewName,
      true
    )
    const appliedSort = this.enhancedSortStore.selectedSortOption
    let sortData = appliedSort?.id ? appliedSort?.id : ''
    if (sortData) {
      sortData = appliedSort.orderBy === SORT_ORDERS.ASC ? '+' + sortData : '-' + sortData
    }
    const isRetail = channelName === ChannelSubSection.Retail
    const body = {
      heirarchy: {
        sort: parseAppliedSort(this.enhancedSortStore.selectedSortOption),
        filters: {
          ...mapNewFiltersToIds(this.cnfProductFilterStore.selected),
          ...(isRetail ? { 'buyingSession-id': [buyingSession] } : {})
        },
        excludeFilters: {
          ...mapNewFiltersToIds(this.cnfProductFilterStore.excludedFilters || {})
        },
        orderProductOrFilters: {
          filters: {
            [defaultValuesFromConfig?.isActive ?? 'original-active']: [true],
            channels: [getTitleCaseChannelName(clientType)],
            allClustersExcluded: [false]
          },
          excludeFilters: {
            'excludedClients-id': [clientId]
          }
        },
        ...(isEmptyValue(this.searchKey)
          ? {}
          : {
              search: this.searchKey,
              searchAttributes: this.searchKey ? searchableAttributes : []
            }),
        groupBy: addedGroups
          .filter(({ id }) => id)
          .map(({ id, sortId }) => ({ attributeId: id, orderBy: sortId })),
        metricAttributes: METRIC_ATTRIBUTES
      },
      orderTopbarKPIs: {
        metricAttributes: KPI_TOPBAR_METRIC_ATTRIBUTES,
        groupBy: [
          orderType === OrderType.Door
            ? DOOR_ORDER_LEVEL_GROUP_ATTR
            : CLIENT_ORDER_LEVEL_GROUP_ATTR
        ]
      }
    }

    return this.apiService
      .fetchAddProductsToOrderGroups(
        buyingSessionGroupId,
        orderId,
        orderType,
        clientOrderId,
        body
      )
      .then(resp => {
        const tempData = cloneDeep(resp?.data?.heirarchy)
        removeSkippedGroups(tempData)
        formatProductGroupsData(tempData, defaultValuesFromConfig?.empty)
        const topbarData = {
          ...resp?.data?.orderTopbarKPIs,
          totalProducts:
            resp?.data?.orderTopbarKPIs.totalProducts ??
            resp?.data?.orderTopbarKPIs.countOfClientOrderProductId
        }
        orderKPIGlobalTopBarStore.setEnhancedOrderKPIsForGlobalTopBar(topbarData)
        if (forRefresh) {
          this.heirarchy = tempData?.children ?? []
        }
        return tempData?.children ?? []
      })
      .catch(e => {
        console.error(e)
        throw new Error('bb.settings.productCardConfigSettings.fetchHierarchy')
      })
  }

  @action
  fetchAndPrepareFilterAttributesData = viewName => {
    const {
      nav: {
        queryParams: {
          buyingSessionGroupId,
          clientType,
          clientOrderId,
          orderId,
          orderType,
          clientId,
          buyingSession
        }
      },
      productConfiguration: { getActionAttributes, defaultValuesFromConfig }
    } = stores

    const filterAttributes = getActionAttributes(
      'filters',
      getChannelName(clientType),
      viewName
    )
    const aggregateAttributes = map(filterAttributes, 'id')
    const channelName = getChannelName(clientType)
    const searchableAttributes = getActionAttributes(
      'searchBy',
      channelName,
      this.currentViewName,
      true
    )
    const isRetail = channelName === ChannelSubSection.Retail
    const requestBody = {
      aggregateAttributes: aggregateAttributes,
      orderProductOrFilters: {
        filters: {
          [defaultValuesFromConfig?.isActive ?? 'original-active']: [true],
          channels: [getTitleCaseChannelName(clientType)],
          allClustersExcluded: [false]
        },
        excludeFilters: {
          'excludedClients-id': [clientId]
        }
      },
      filters: {
        ...(isRetail ? { 'buyingSession-id': [buyingSession] } : {})
      },
      ...(isEmptyValue(this.searchKey)
        ? {}
        : {
            search: this.searchKey,
            searchAttributes: this.searchKey ? searchableAttributes : []
          })
    }
    const orderPath =
      orderType === OrderType.Client
        ? `/clientOrder/${orderId}/aggregate-attributes`
        : `/clientOrder/${clientOrderId}/doorOrder/${orderId}/aggregate-attributes`
    const url = `${config.buyingBoardServiceRestEndpoint}${PRODUCT_SERVICE_ENDPOINTS.ENHANCED_PRODUCT_COMMON_PATH}/${buyingSessionGroupId}${orderPath}`

    if (aggregateAttributes.length) {
      this.cnfProductFilterStore.fetchFilterAttributeValues(
        url,
        requestBody,
        filterAttributes,
        aggregateAttributes
      )
    }
  }

  @action
  toggleProductIsOrderStatus = (productId: string, status?: boolean) => {
    const product = this.itemIndexObjectStore.idObjectMap.get(productId)
    const updatedProduct = {
      ...product,
      ...{ isOrderProductExist: isNil(status) ? !product?.isOrderProductExist : status }
    }
    this.itemIndexObjectStore.indexObjectMap.set(
      this.itemIndexObjectStore.getIndexByKey(productId),
      updatedProduct
    )
    this.itemIndexObjectStore.idObjectMap.set(productId, updatedProduct)
  }

  @action
  addProductToOrder = async (productId, doorOrderIds?, updateObjectStore?) => {
    const {
      nav: {
        queryParams: { orderId, orderType }
      }
    } = stores

    this.toggleProductIsOrderStatus(productId)

    try {
      let response = null
      if (this.isEnhancedViewEnabled) {
        response = this.addProductToEnhancedOrder(
          productId,
          doorOrderIds,
          updateObjectStore
        ).then(res => {
          this.updateOrderTopbarData(res)
          return res
        })
      } else {
        response = this.apiService
          .addProductToOrder(orderId, productId, orderType)
          .then(response => {
            orderKPIGlobalTopBarStore.updateOrderKPIByOrderTypes({
              clientOrder: {
                ...orderKPIGlobalTopBarStore.orderKPIsForGlobalTopBar.summary
                  ?.clientOrder,
                ...response.clientOrder
              },
              doorOrder: {
                ...orderKPIGlobalTopBarStore.orderKPIsForGlobalTopBar.summary?.doorOrder,
                ...response.doorOrder
              }
            })
          })
      }
      return response
    } catch (error) {
      console.error(error)
      this.toggleProductIsOrderStatus(productId)
    }
  }

  @action
  addAllProductsToOrder = async doorOrderIds => {
    const {
      nav: {
        queryParams: { orderId }
      }
    } = stores

    try {
      let response = null
      if (this.isEnhancedViewEnabled) {
        response = this.addAllProductsToEnhancedClientOrder(doorOrderIds).then(res => {
          this.updateOrderTopbarData(res)
          return res
        })
      } else {
        response = await this.apiService.addProductsToClientOrder(
          orderId,
          this.mapFiltersToIds(this.filterStore.appliedFilters),
          doorOrderIds,
          this.searchKey
        )
        orderKPIGlobalTopBarStore.updateOrderKPIByOrderTypes({
          clientOrder: {
            ...orderKPIGlobalTopBarStore.orderKPIsForGlobalTopBar.summary?.clientOrder,
            ...response.clientOrder
          },
          doorOrder: {
            ...orderKPIGlobalTopBarStore.orderKPIsForGlobalTopBar.summary?.doorOrder,
            ...response.doorOrder
          }
        })
      }

      transaction(() => {
        this.itemIndexObjectStore.objects.forEach(product => {
          if (product?.id) {
            this.toggleProductIsOrderStatus(product.id, true)
          }
        })
      })

      return response
    } catch (error) {
      console.error(error)
    }
    return {}
  }

  @action
  removeAllProductsFromOrder = async doorOrderIds => {
    const {
      nav: {
        queryParams: { orderId, clientType }
      }
    } = stores

    try {
      if (this.isEnhancedViewEnabled) {
        return this.removeAllProductsFromEnhancedClientOrder().then(res => {
          this.processRemoveAllResponse(res, true, clientType)
          return res
        })
      } else {
        return this.apiService
          .removeProductsFromClientOrder(
            orderId,
            this.mapFiltersToIds(this.filterStore.appliedFilters),
            doorOrderIds,
            this.searchKey
          )
          .then(res => {
            this.processRemoveAllResponse(res, false, clientType)
            return res
          })
      }
    } catch (error) {
      console.error(error)
      return undefined
    }
  }

  @action processRemoveAllResponse = (response, isEnhancedView, clientType) => {
    if (isEnhancedView) {
      this.updateOrderTopbarData(response)
    } else {
      orderKPIGlobalTopBarStore.updateOrderKPIByOrderTypes({
        clientOrder: {
          ...orderKPIGlobalTopBarStore.orderKPIsForGlobalTopBar.summary?.clientOrder,
          ...response.clientOrder
        },
        doorOrder: {
          ...orderKPIGlobalTopBarStore.orderKPIsForGlobalTopBar.summary?.doorOrder,
          ...response.doorOrder
        }
      })
    }
    transaction(() => {
      /**
       * non core products for retail client aren't removed
       * hence those needn't be added
       */
      const nonCoreProducts =
        clientType === getEnumValue(VIPClientType.Internal)
          ? this.itemIndexObjectStore.objects.filter(product => !product.isCore)
          : this.itemIndexObjectStore.objects

      nonCoreProducts.forEach(product => {
        if (product?.id) {
          this.toggleProductIsOrderStatus(product.id, false)
        }
      })
    })
  }

  @action
  addAllProductsToDoorOrder = async doorOrderIds => {
    const {
      nav: {
        queryParams: { clientOrderId }
      }
    } = stores

    try {
      let response = null
      if (this.isEnhancedViewEnabled) {
        response = this.addAllProductsToEnhancedDoorOrder().then(res => {
          this.updateOrderTopbarData(res)
          return res
        })
      } else {
        response = await this.apiService.addProductsToDoorOrder(
          clientOrderId,
          this.mapFiltersToIds(this.filterStore.appliedFilters),
          doorOrderIds,
          this.searchKey
        )
        orderKPIGlobalTopBarStore.updateOrderKPIByOrderTypes({
          clientOrder: {
            ...orderKPIGlobalTopBarStore.orderKPIsForGlobalTopBar.summary?.clientOrder,
            ...response.clientOrder
          },
          doorOrder: {
            ...orderKPIGlobalTopBarStore.orderKPIsForGlobalTopBar.summary?.doorOrder,
            ...response.doorOrder
          }
        })
      }
      transaction(() => {
        this.itemIndexObjectStore.objects.forEach(product => {
          if (product?.id) {
            this.toggleProductIsOrderStatus(product.id, true)
          }
        })
      })
      return response
    } catch (error) {
      console.error(error)
    }
    return {}
  }

  @action
  removeAllProductsFromDoorOrder = async doorOrderId => {
    const {
      nav: {
        queryParams: { clientOrderId }
      }
    } = stores

    try {
      let response = null
      if (this.isEnhancedViewEnabled) {
        response = this.removeAllProductsFromEnhancedDoorOrder().then(res => {
          this.updateOrderTopbarData(res)
          return res
        })
      } else {
        response = await this.apiService.removeProductsFromDoorOrder(
          clientOrderId,
          this.mapFiltersToIds(this.filterStore.appliedFilters),
          doorOrderId,
          this.searchKey
        )
        orderKPIGlobalTopBarStore.updateOrderKPIByOrderTypes({
          clientOrder: {
            ...orderKPIGlobalTopBarStore.orderKPIsForGlobalTopBar.summary?.clientOrder,
            ...response.clientOrder
          },
          doorOrder: {
            ...orderKPIGlobalTopBarStore.orderKPIsForGlobalTopBar.summary?.doorOrder,
            ...response.doorOrder
          }
        })
      }

      transaction(() => {
        this.itemIndexObjectStore.objects.forEach(product => {
          if (product?.id) {
            this.toggleProductIsOrderStatus(product.id, false)
          }
        })
      })

      return response
    } catch (error) {
      console.error(error)
    }
    return {}
  }

  @action
  removeProductToOrder = async (
    productId: any,
    groupId?: string,
    isProductRemoved?: boolean,
    updateObjectStore?
  ) => {
    this.toggleProductIsOrderStatus(productId)
    try {
      let response = null
      if (this.isEnhancedViewEnabled) {
        response = this.removeProductFromEnhancedOrder(productId, updateObjectStore).then(
          res => {
            if (isProductRemoved && groupId) {
              this.removeProductItemFromPage(productId, groupId)
            }
            this.updateOrderTopbarData(res)
            return res
          }
        )
      } else {
        response = this.removeItemAPI(productId).then(response => {
          if (isProductRemoved) {
            this.removeProductItemFromPage(productId, groupId)
          }
          orderKPIGlobalTopBarStore.updateOrderKPIByOrderTypes({
            clientOrder: response.clientOrder,
            doorOrder: response.doorOrder
          })
        })
      }
      return response
    } catch (error) {
      console.error(error)
      this.toggleProductIsOrderStatus(productId)
    }
  }

  removeProductItemFromPage = (productId, groupId) => {
    transaction(() => {
      const productIndex = this.itemIndexObjectStore.getIndexByKey(productId)
      // Remove Hierarchy
      this.removeHierarchy(groupId.split(CATEGORY_INTERNAL_SEPERATOR))
      // Remove Product
      this.itemIndexObjectStore.clearDataByRange(productIndex, productIndex)
    })
  }

  removeItemAPI = (productId: string) => {
    const {
      nav: {
        queryParams: { orderId, orderType }
      }
    } = stores
    return this.apiService.removeProductToOrder(
      orderId,
      productId,
      orderType as OrderType
    )
  }

  onProductRemove = () => {}

  isProductPresentInOrder = computedFn(id => {
    return this.itemIndexObjectStore.idObjectMap.get(id)?.isOrderProductExist
  })

  async init() {
    super.init()
  }

  fetchFilterAttributesFromAPI = () => {
    const {
      nav: {
        queryParams: { orderId, buyingSessionGroupId, orderType }
      }
    } = stores

    if (!orderId) return undefined
    // Do not call old filters API for an enhanced view
    if (this.isEnhancedViewEnabled) {
      return undefined
    }
    return this.apiService.fetchCollectionFilterAttributes(
      buyingSessionGroupId,
      orderId,
      orderType as OrderType
    )
  }
  @action
  getSearchTextParam = () => {
    return isEmptyValue(this.searchKey)
      ? {}
      : {
          search: this.searchKey
        }
  }

  @action
  addAllProductsToEnhancedClientOrder = doorOrderIds => {
    const {
      nav: {
        queryParams: {
          orderId,
          buyingSessionGroupId,
          clientType,
          buyingSession,
          clientId
        }
      }
    } = stores

    const isRetail = getChannelName(clientType) === ChannelSubSection.Retail
    const body = {
      doorOrderIds,
      filters: {
        ...mapNewFiltersToIds(this.cnfProductFilterStore.selected),
        ...(isRetail ? { 'buyingSession-id': [buyingSession] } : {}),
        'original-active': [true],
        channels: [getTitleCaseChannelName(clientType)],
        allClustersExcluded: [false]
      },
      excludeFilters: {
        ...mapNewFiltersToIds(this.cnfProductFilterStore.excludedFilters || {}),
        'excludedClients-id': [clientId]
      },
      ...this.getSearchTextParam(),
      ...CLIENT_KPI_ORDER_LEVEL_AGGR
    }
    return this.apiService
      .addProductsToEnhancedClientOrder(orderId, buyingSessionGroupId, body)
      .then(resp => resp)
  }

  @action
  addAllProductsToEnhancedDoorOrder = () => {
    const {
      nav: {
        queryParams: {
          clientOrderId,
          orderId,
          buyingSessionGroupId,
          clientType,
          buyingSession,
          clientId
        }
      }
    } = stores

    const isRetail = getChannelName(clientType) === ChannelSubSection.Retail
    const body = {
      filters: {
        ...mapNewFiltersToIds(this.cnfProductFilterStore.selected),
        ...(isRetail ? { 'buyingSession-id': [buyingSession] } : {}),
        'original-active': [true],
        channels: [getTitleCaseChannelName(clientType)],
        allClustersExcluded: [false]
      },
      excludeFilters: {
        ...mapNewFiltersToIds(this.cnfProductFilterStore.excludedFilters || {}),
        'excludedClients-id': [clientId]
      },
      ...this.getSearchTextParam(),
      ...DOOR_KPI_ORDER_LEVEL_AGGR
    }
    return this.apiService
      .addProductsToEnhancedDoorOrder(clientOrderId, buyingSessionGroupId, orderId, body)
      .then(resp => resp)
  }

  @action
  removeAllProductsFromEnhancedClientOrder = () => {
    const {
      nav: {
        queryParams: { orderId, buyingSessionGroupId, clientType }
      }
    } = stores

    const isRetail = getChannelName(clientType) === ChannelSubSection.Retail
    const body = {
      filters: {
        ...mapNewFiltersToIds(this.cnfProductFilterStore.selected),
        ...(isRetail ? { isCore: [false] } : {})
      },
      excludeFilters: {
        ...mapNewFiltersToIds(this.cnfProductFilterStore.excludedFilters || {})
      },
      ...this.getSearchTextParam(),
      ...CLIENT_KPI_ORDER_LEVEL_AGGR
    }
    return this.apiService
      .removeAllProductsFromEnhancedClientOrder(orderId, buyingSessionGroupId, body)
      .then(resp => resp)
  }

  @action
  removeAllProductsFromEnhancedDoorOrder = () => {
    const {
      nav: {
        queryParams: { clientOrderId, orderId, buyingSessionGroupId }
      }
    } = stores
    const body = {
      filters: {
        ...mapNewFiltersToIds(this.cnfProductFilterStore.selected)
      },
      excludeFilters: {
        ...mapNewFiltersToIds(this.cnfProductFilterStore.excludedFilters || {})
      },
      ...this.getSearchTextParam(),
      ...DOOR_KPI_ORDER_LEVEL_AGGR
    }

    return this.apiService
      .removeAllProductsFromEnhancedDoorOrder(
        clientOrderId,
        buyingSessionGroupId,
        orderId,
        body
      )
      .then(resp => resp)
  }

  @action
  addProductToEnhancedOrder = (productId, doorOrderIds?, updateObjectStore?) => {
    const {
      nav: {
        queryParams: { clientOrderId, orderId, buyingSessionGroupId, orderType }
      }
    } = stores
    const orderAggregation =
      orderType === OrderType.Door
        ? { ...DOOR_KPI_ORDER_LEVEL_AGGR }
        : { ...CLIENT_KPI_ORDER_LEVEL_AGGR, doorOrderIds }

    const body = {
      filters: {
        ...mapNewFiltersToIds(this.cnfProductFilterStore.selected)
      },
      excludeFilters: {
        ...mapNewFiltersToIds(this.cnfProductFilterStore.excludedFilters || {})
      },
      ...this.getSearchTextParam(),
      ...orderAggregation
    }

    return this.apiService
      .addProductToEnhancedOrder(
        productId,
        orderId,
        buyingSessionGroupId,
        clientOrderId,
        body,
        orderType
      )
      .then(resp => {
        if (updateObjectStore) {
          set(
            this.itemIndexObjectStore.idObjectMap?.get?.(productId),
            'isOrderProductExist',
            true
          )
        }
        return resp
      })
  }

  @action
  removeProductFromEnhancedOrder = (productId, updateObjectStore?) => {
    const {
      nav: {
        queryParams: { clientOrderId, orderId, buyingSessionGroupId, orderType }
      }
    } = stores
    const orderAggregation =
      orderType === OrderType.Door
        ? { ...DOOR_KPI_ORDER_LEVEL_AGGR }
        : { ...CLIENT_KPI_ORDER_LEVEL_AGGR }

    const body = {
      filters: {
        ...mapNewFiltersToIds(this.cnfProductFilterStore.selected)
      },
      excludeFilters: {
        ...mapNewFiltersToIds(this.cnfProductFilterStore.excludedFilters || {})
      },
      ...this.getSearchTextParam(),
      ...orderAggregation
    }

    return this.apiService
      .removeProductFromEnhancedOrder(
        productId,
        orderId,
        buyingSessionGroupId,
        clientOrderId,
        body,
        orderType
      )
      .then(resp => {
        if (updateObjectStore) {
          set(
            this.itemIndexObjectStore.idObjectMap?.get?.(productId),
            'isOrderProductExist',
            false
          )
        }
        return resp
      })
  }

  @action updateOrderTopbarData = (respData?) => {
    if (respData) {
      let temp = respData?.orderTopbarKPIs
      if (temp) {
        temp = {
          ...temp,
          totalProducts: temp.totalProducts ?? temp.countOfClientOrderProductId
        }
      }
      orderKPIGlobalTopBarStore.setEnhancedOrderKPIsForGlobalTopBar(temp)
    } else {
      const {
        nav: {
          queryParams: { clientOrderId, orderId, orderType, buyingSessionGroupId }
        }
      } = stores
      this.orderService
        .fetchEnhancedOrderTopbarData(
          buyingSessionGroupId,
          orderId,
          clientOrderId,
          orderType
        )
        .then(res => {
          const temp = {
            ...res,
            totalProducts: res.totalProducts ?? res.countOfClientOrderProductId
          }
          orderKPIGlobalTopBarStore.setEnhancedOrderKPIsForGlobalTopBar(temp)
        })
    }
  }
}
